import { useEffect, useState } from "react";
import { AdminLayout } from "../../components/AdminLayout";
import { DataTable } from "../../components/DataTable";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { adminHeaders, api } from "../../lib/api";
import { prettyRawJson, reviewStatusLabel, rewardStatusLabel, rewardTypeLabel } from "../../lib/adminDisplayLabels";
import { isLocalDevFrontend } from "../../lib/env";
import { money, shortDate } from "../../lib/format";

interface CrawlerCursorRow {
  id: number;
  source_code: string;
  cursor_type: string;
  cursor_value?: string | null;
  status: string;
  updated_at: string;
}

interface RawGiftRecordRow {
  id: number;
  external_id?: string | null;
  member_account: string;
  gift_type: string;
  amount: number;
  occurred_at: string;
  raw_json?: string | null;
  created_at: string;
  member_exists?: boolean | number | null;
  review_id?: number | null;
  review_status?: string | null;
  confirmed_amount?: number | null;
  review_reward_type?: string | null;
  review_reviewed_at?: string | null;
  reward_id?: number | null;
  reward_status?: string | null;
  reward_amount?: number | null;
  claimed_at?: string | null;
}

interface RewardCrawlerDispatchTaskRow {
  id: number;
  reward_id: number;
  member_account: string;
  reward_type: string;
  amount: number;
  dispatch_mode: string;
  dispatch_status: string;
  dispatch_method: string;
  scheduled_at?: string | null;
  queued_at?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  failed_at?: string | null;
  failed_reason?: string | null;
  attempt_count: number;
  last_attempt_at?: string | null;
  crawler_reference_id?: string | null;
  crawler_payload_json?: string | null;
  crawler_response_json?: string | null;
  created_at: string;
}

interface RewardNotificationRow {
  task_id: number;
  reward_id: number;
  member_account: string;
  reward_type: string;
  amount: number;
  dispatch_status: string;
  reward_status: string;
  completed_at?: string | null;
  claimed_at?: string | null;
  crawler_reference_id?: string | null;
  notification_id?: number | null;
  notification_status?: string | null;
  notified_at?: string | null;
  notified_by?: string | null;
  message_text?: string | null;
  note?: string | null;
}

interface CrawlerData {
  cursors: CrawlerCursorRow[];
  gift_records: RawGiftRecordRow[];
  reward_dispatch_tasks?: RewardCrawlerDispatchTaskRow[];
  reward_notifications?: RewardNotificationRow[];
}

type RewardDispatchTaskAction = "retry" | "cancel" | "manual_completed" | "simulate_completed" | "simulate_failed";

interface PendingTaskAction {
  task: RewardCrawlerDispatchTaskRow;
  action: RewardDispatchTaskAction;
}

const PROCESSING_TIMEOUT_MINUTES = 30;
const MAX_ATTEMPT_COUNT = 3;

export function AdminCrawlerPage() {
  const isLocalDev = isLocalDevFrontend();
  const [cursors, setCursors] = useState<CrawlerCursorRow[]>([]);
  const [giftRecords, setGiftRecords] = useState<RawGiftRecordRow[]>([]);
  const [rewardDispatchTasks, setRewardDispatchTasks] = useState<RewardCrawlerDispatchTaskRow[]>([]);
  const [rewardNotifications, setRewardNotifications] = useState<RewardNotificationRow[]>([]);
  const [taskStatusFilter, setTaskStatusFilter] = useState("all");
  const [taskSearchInput, setTaskSearchInput] = useState("");
  const [taskSearchQuery, setTaskSearchQuery] = useState("");
  const [notificationCategoryFilter, setNotificationCategoryFilter] = useState("high_touch");
  const [notificationStatusFilter, setNotificationStatusFilter] = useState("all");
  const [notificationDateFilter, setNotificationDateFilter] = useState("all");
  const [notificationSearchInput, setNotificationSearchInput] = useState("");
  const [notificationSearchQuery, setNotificationSearchQuery] = useState("");
  const [confirmedAmounts, setConfirmedAmounts] = useState<Record<number, string>>({});
  const [adminNotes, setAdminNotes] = useState<Record<number, string>>({});
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [taskActionProcessingId, setTaskActionProcessingId] = useState<number | null>(null);
  const [pendingTaskAction, setPendingTaskAction] = useState<PendingTaskAction | null>(null);
  const [notificationProcessingTaskId, setNotificationProcessingTaskId] = useState<number | null>(null);
  const [manualCopyText, setManualCopyText] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  async function load() {
    setError("");
    const data = await api<CrawlerData>("/api/admin/crawler", { headers: adminHeaders() });
    setCursors(data.cursors ?? []);
    setGiftRecords(data.gift_records ?? []);
    setRewardDispatchTasks(data.reward_dispatch_tasks ?? []);
    setRewardNotifications(data.reward_notifications ?? []);
    setConfirmedAmounts((previous) => {
      const next = { ...previous };
      for (const row of data.gift_records ?? []) {
        if (next[row.id] == null) next[row.id] = String(row.amount ?? "");
      }
      return next;
    });
  }

  async function createGiftReview(row: RawGiftRecordRow) {
    setMessage("");
    setError("");
    setManualCopyText("");
    const amountText = confirmedAmounts[row.id] ?? "";
    const confirmedAmount = Number(amountText.replace(/,/g, "").trim());
    if (!Number.isFinite(confirmedAmount) || confirmedAmount <= 0) {
      setError("確認派發金額必須大於 0。");
      return;
    }
    if (!row.external_id) {
      setError("此原始贈禮缺少外部紀錄 ID，不能建立審核。");
      return;
    }
    if (isMissingMember(row)) {
      setError("找不到會員資料，無法建立審核。");
      return;
    }
    if (row.gift_type !== "gift_helper") {
      setError("此贈禮類型目前不支援建立派發審核。");
      return;
    }
    const ok = window.confirm([
      "請確認要建立贈禮小幫手審核：",
      `會員：${row.member_account}`,
      `外部紀錄 ID：${row.external_id}`,
      `原始金額：${money(row.amount)}`,
      `確認派發金額：${money(confirmedAmount)}`,
      "建立後仍需在審核中心通過；通過後才會建立可派發獎勵。"
    ].join("\n"));
    if (!ok) return;

    setProcessingId(row.id);
    try {
      await api("/api/admin/crawler/gift-records/review", {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify({
          raw_gift_record_id: row.id,
          confirmed_amount: confirmedAmount,
          admin_note: adminNotes[row.id] ?? ""
        })
      });
      setMessage(`已建立贈禮審核：${row.member_account} / ${money(confirmedAmount)}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "建立贈禮審核失敗");
    } finally {
      setProcessingId(null);
    }
  }

  async function copyNotificationMessage(row: RewardNotificationRow) {
    setMessage("");
    setError("");
    const text = notificationMessage(row);
    const copied = await copyTextSafely(text);
    if (copied) {
      setManualCopyText("");
      setMessage("已複製通知話術。");
    } else {
      setManualCopyText(text);
      setError("無法自動複製，請手動複製下方話術。");
    }
  }

  async function markNotificationNotified(row: RewardNotificationRow) {
    setMessage("");
    setError("");
    setManualCopyText("");
    setNotificationProcessingTaskId(row.task_id);
    try {
      const result = await api<{ created: boolean; notification: Record<string, unknown> }>("/api/admin/crawler/reward-notifications/mark-notified", {
        method: "POST",
        headers: adminHeaders(),
        body: JSON.stringify({
          task_id: row.task_id,
          message_text: notificationMessage(row)
        })
      });
      setMessage(result.created ? `已標記通知：任務 #${row.task_id}` : `此任務已通知：任務 #${row.task_id}`);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "標記已通知失敗");
    } finally {
      setNotificationProcessingTaskId(null);
    }
  }

  async function confirmTaskAction() {
    if (!pendingTaskAction) return;
    setMessage("");
    setError("");
    setManualCopyText("");
    const { task, action } = pendingTaskAction;
    setTaskActionProcessingId(task.id);
    try {
      const endpoint = taskActionEndpoint(task.id, action);
      await api(endpoint, {
        method: "POST",
        headers: adminHeaders(),
        ...(false ? { body: action === "manual_completed"
          ? JSON.stringify({ note: "客服人工確認贈禮小幫手派發完成" })
          : undefined } : {}),
        body: taskActionBody(action)
      });
      setMessage(taskActionSuccessMessageSafe(action, task.id));
      setPendingTaskAction(null);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "派發任務操作失敗");
    } finally {
      setTaskActionProcessingId(null);
    }
  }

  useEffect(() => {
    load().catch((err) => setError(err instanceof Error ? err.message : "讀取爬蟲資料失敗"));
  }, []);

  const filteredRewardDispatchTasks = filterRewardCrawlerTasks(rewardDispatchTasks, taskStatusFilter, taskSearchQuery);
  const trimmedTaskSearchQuery = taskSearchQuery.trim();
  const filteredRewardNotifications = filterRewardNotifications(
    rewardNotifications,
    notificationCategoryFilter,
    notificationStatusFilter,
    notificationDateFilter,
    notificationSearchQuery
  );
  const trimmedNotificationSearchQuery = notificationSearchQuery.trim();

  return (
    <AdminLayout title="爬蟲資料 / 贈禮小幫手">
      {message ? <p className="result">{message}</p> : null}
      {error ? <p className="error-box">{error}</p> : null}
      {manualCopyText ? (
        <section className="manual-copy-panel" aria-label="手動複製通知話術">
          <strong>手動複製話術</strong>
          <p>自動複製未成功時，請直接複製下方文字。</p>
          <textarea
            readOnly
            value={manualCopyText}
            onFocus={(event) => event.currentTarget.select()}
          />
        </section>
      ) : null}

      <section className="admin-card">
        <div className="admin-card-head">
          <span>贈禮小幫手</span>
          <h2>贈禮小幫手原始紀錄處理台</h2>
        </div>
        <div className="summary-list">
          <p>贈禮原始紀錄只作為原始證據；原始金額只作參考，請客服人工確認實際要派發的金額。</p>
          <p>建立審核後不會立即建立派發單。需在審核中心通過後，才建立「贈禮小幫手獎勵」可派發單，並由派發中心確認已派發。</p>
        </div>
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>贈禮小幫手派發任務</span>
          <h2>贈禮小幫手派發任務</h2>
        </div>
        <p className="admin-muted">
          這裡只追蹤已排入贈禮小幫手的派發任務。派發成功後會回寫為已完成；派發失敗時，獎勵會保留待派發，方便後續人工處理或重試。
        </p>
        <form
          className="admin-filter-bar crawler-task-search-bar"
          onSubmit={(event) => {
            event.preventDefault();
            setTaskSearchQuery(taskSearchInput.trim());
          }}
        >
          <label>
            搜尋派發任務
            <input
              value={taskSearchInput}
              onChange={(event) => setTaskSearchInput(event.target.value)}
              placeholder="搜尋會員帳號 / 獎勵 ID / 任務 ID / 獎勵類型 / 回傳編號"
            />
          </label>
          <button type="submit">查詢</button>
          <button
            type="button"
            onClick={() => {
              setTaskSearchInput("");
              setTaskSearchQuery("");
              setTaskStatusFilter("all");
            }}
          >
            清除
          </button>
        </form>
        <div className="tabs admin-status-tabs">
          {["all", "pending", "processing", "completed", "failed", "canceled"].map((status) => (
            <button
              key={status}
              className={taskStatusFilter === status ? "active" : ""}
              onClick={() => setTaskStatusFilter(status)}
            >
              {crawlerTaskStatusFilterLabel(status)}
            </button>
          ))}
        </div>
        <div className="admin-active-filters">
          <span>搜尋：{trimmedTaskSearchQuery || "全部"}</span>
          <span>狀態：{crawlerTaskStatusFilterLabel(taskStatusFilter)}</span>
          <span>筆數：{filteredRewardDispatchTasks.length}</span>
        </div>
        <RewardCrawlerDispatchTaskCards
          tasks={filteredRewardDispatchTasks}
          isLocalDev={isLocalDev}
          processingTaskId={taskActionProcessingId}
          emptyMessage={trimmedTaskSearchQuery ? "查無符合條件的派發任務。" : "目前沒有符合條件的派發任務。"}
          onRequestAction={(task, action) => setPendingTaskAction({ task, action })}
        />
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>會員通知</span>
          <h2>已派發可通知會員清單</h2>
        </div>
        <p className="admin-muted">
          只顯示已派發完成且獎勵已完成的資料。等待派發、派發中、派發失敗或已取消的任務不會出現在此清單，避免誤通知會員。
        </p>
        <form
          className="admin-filter-bar"
          onSubmit={(event) => {
            event.preventDefault();
            setNotificationSearchQuery(notificationSearchInput.trim());
          }}
        >
          <label>
            搜尋通知資料
            <input
              value={notificationSearchInput}
              onChange={(event) => setNotificationSearchInput(event.target.value)}
              placeholder="搜尋會員帳號 / 獎勵 ID / 任務 ID / 獎勵類型"
            />
          </label>
          <label>
            通知分類
            <select value={notificationCategoryFilter} onChange={(event) => setNotificationCategoryFilter(event.target.value)}>
              {notificationCategoryOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </label>
          <label>
            通知狀態
            <select value={notificationStatusFilter} onChange={(event) => setNotificationStatusFilter(event.target.value)}>
              <option value="all">全部</option>
              <option value="unnotified">未通知</option>
              <option value="notified">已通知</option>
            </select>
          </label>
          <label>
            完成時間
            <select value={notificationDateFilter} onChange={(event) => setNotificationDateFilter(event.target.value)}>
              <option value="all">全部</option>
              <option value="today">今日</option>
              <option value="last7">近 7 天</option>
            </select>
          </label>
          <button type="submit">查詢</button>
          <button
            type="button"
            onClick={() => {
              setNotificationSearchInput("");
              setNotificationSearchQuery("");
              setNotificationCategoryFilter("high_touch");
              setNotificationStatusFilter("all");
              setNotificationDateFilter("all");
            }}
          >
            清除
          </button>
        </form>
        <div className="admin-active-filters">
          <span>分類：{notificationCategoryLabel(notificationCategoryFilter)}</span>
          <span>通知狀態：{notificationStatusFilterLabel(notificationStatusFilter)}</span>
          <span>完成時間：{notificationDateFilterLabel(notificationDateFilter)}</span>
          <span>搜尋：{trimmedNotificationSearchQuery || "全部"}</span>
          <span>筆數：{filteredRewardNotifications.length}</span>
        </div>
        <RewardNotificationTable
          rows={filteredRewardNotifications}
          processingTaskId={notificationProcessingTaskId}
          onCopy={copyNotificationMessage}
          onMarkNotified={markNotificationNotified}
        />
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>贈禮原始紀錄</span>
          <h2>贈禮原始紀錄</h2>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>ID / 外部紀錄 ID</th>
                <th>會員</th>
                <th>贈禮類型</th>
                <th>原始金額</th>
                <th>發生時間</th>
                <th>處理狀態</th>
                <th>確認金額 / 備註</th>
                <th>操作</th>
                <th>原始資料</th>
              </tr>
            </thead>
            <tbody>
              {giftRecords.map((row) => {
                const canCreateReview = !row.review_id && !row.reward_id && Boolean(row.external_id) && !isMissingMember(row) && row.gift_type === "gift_helper";
                const operationLabel = giftRecordOperationLabel(row);
                return (
                  <tr key={row.id}>
                    <td>
                      <strong>#{row.id}</strong>
                      <br />
                      <span className="admin-muted">{row.external_id ?? "缺少外部紀錄 ID"}</span>
                    </td>
                    <td><a className="admin-table-link" href={`/admin/members/${encodeURIComponent(row.member_account)}`}>{row.member_account}</a></td>
                    <td>{giftTypeLabel(row.gift_type)}</td>
                    <td>{money(row.amount)}</td>
                    <td>{shortDate(row.occurred_at)}</td>
                    <td>{giftRecordStatus(row)}</td>
                    <td>
                      {canCreateReview ? (
                        <div className="stacked-fields">
                          <input
                            type="number"
                            min="1"
                            step="1"
                            value={confirmedAmounts[row.id] ?? ""}
                            onChange={(event) => setConfirmedAmounts((current) => ({ ...current, [row.id]: event.target.value }))}
                            aria-label="確認派發金額"
                          />
                          <textarea
                            value={adminNotes[row.id] ?? ""}
                            onChange={(event) => setAdminNotes((current) => ({ ...current, [row.id]: event.target.value }))}
                            placeholder="審核備註，可留空"
                            rows={2}
                          />
                          <small className="admin-muted">原始金額只作參考，請確認實際派發金額後再建立審核。</small>
                        </div>
                      ) : (
                        <span>{row.confirmed_amount ? money(row.confirmed_amount) : "-"}</span>
                      )}
                    </td>
                    <td>
                      {canCreateReview ? (
                        <button onClick={() => createGiftReview(row)} disabled={processingId === row.id}>
                          {processingId === row.id ? "建立中..." : "建立贈禮審核"}
                        </button>
                      ) : (
                        <span className="closed-status">{operationLabel}</span>
                      )}
                    </td>
                    <td>
                      <details className="raw-details">
                        <summary>查看</summary>
                        <pre>{prettyRawJson(row.raw_json)}</pre>
                      </details>
                    </td>
                  </tr>
                );
              })}
              {!giftRecords.length ? <tr><td colSpan={9}>目前沒有贈禮原始紀錄。</td></tr> : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <span>爬蟲同步狀態</span>
          <h2>爬蟲同步狀態</h2>
        </div>
        <DataTable rows={cursors as unknown as Record<string, unknown>[]} />
      </section>
      <ConfirmDialog
        open={Boolean(pendingTaskAction)}
        title={pendingTaskAction ? taskActionConfirmCopySafe(pendingTaskAction.action).title : ""}
        message={pendingTaskAction ? taskActionConfirmCopySafe(pendingTaskAction.action).message : ""}
        confirmText={pendingTaskAction ? taskActionConfirmCopy(pendingTaskAction.action).confirmText : "確認"}
        variant={pendingTaskAction?.action === "retry" || pendingTaskAction?.action === "simulate_completed" ? "normal" : "danger"}
        loading={Boolean(pendingTaskAction && taskActionProcessingId === pendingTaskAction.task.id)}
        onCancel={() => {
          if (taskActionProcessingId == null) setPendingTaskAction(null);
        }}
        onConfirm={confirmTaskAction}
      />
    </AdminLayout>
  );
}

function RewardNotificationTable({
  rows,
  processingTaskId,
  onCopy,
  onMarkNotified
}: {
  rows: RewardNotificationRow[];
  processingTaskId: number | null;
  onCopy: (row: RewardNotificationRow) => void;
  onMarkNotified: (row: RewardNotificationRow) => void;
}) {
  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>完成時間</th>
            <th>會員帳號</th>
            <th>獎勵類型</th>
            <th>分類</th>
            <th>金額</th>
            <th>獎勵 ID / 任務 ID</th>
            <th>通知狀態</th>
            <th>已通知時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const notified = isNotificationMarked(row);
            const processing = processingTaskId === row.task_id;
            return (
              <tr key={row.task_id}>
                <td>{shortDate(row.completed_at ?? row.claimed_at)}</td>
                <td>
                  <a className="admin-table-link" href={`/admin/members/${encodeURIComponent(row.member_account)}`}>
                    {row.member_account}
                  </a>
                </td>
                <td>{rewardTypeLabel(row.reward_type)}</td>
                <td>{notificationCategoryLabelsForReward(row.reward_type).join(" / ")}</td>
                <td>{money(row.amount)}</td>
                <td>
                  <a className="admin-table-link" href={`/admin/rewards?member_account=${encodeURIComponent(row.member_account)}`}>
                    獎勵 #{row.reward_id}
                  </a>
                  <br />
                  <span className="admin-muted">任務 #{row.task_id}</span>
                </td>
                <td>{notified ? "已通知" : "未通知"}</td>
                <td>{notified ? shortDate(row.notified_at) : "-"}</td>
                <td>
                  <div className="inline-actions">
                    <button className="secondary mini-button" type="button" onClick={() => onCopy(row)}>
                      複製通知話術
                    </button>
                    {notified ? (
                      <button className="secondary mini-button" type="button" disabled>
                        已通知
                      </button>
                    ) : (
                      <button className="mini-button" type="button" onClick={() => onMarkNotified(row)} disabled={processing}>
                        {processing ? "處理中..." : "標記已通知"}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            );
          })}
          {!rows.length ? <tr><td colSpan={9}>目前沒有符合條件的可通知資料。</td></tr> : null}
        </tbody>
      </table>
    </div>
  );
}

const notificationCategoryOptions = [
  { value: "high_touch", label: "高感受通知" },
  { value: "deposit_rebate", label: "存款回饋" },
  { value: "red_packet", label: "紅包券獎勵" },
  { value: "invite", label: "邀友獎勵" },
  { value: "newbie", label: "新人 / 畢業獎勵" },
  { value: "manual_compensation", label: "人工補發 / 補償" },
  { value: "all", label: "全部" }
];

const highTouchNotificationTypes = new Set([
  "birthday_bonus_manual",
  "rescue_fund_monthly",
  "manual_compensation",
  "gift_helper_bonus",
  "newbie_step7_graduation_gift"
]);

const depositNotificationTypes = new Set([
  "regular_deposit_rebate"
]);

const redPacketNotificationTypes = new Set([
  "newbie_step6_red_packet_draw",
  "newbie_bonus_red_packet_draw",
  "graduate_launch_red_packet_draw",
  "advanced_launch_red_packet_draw",
  "weekly_bright_red_packet_draw",
  "weekend_bright_red_packet_draw",
  "weekend_advanced_red_packet_draw"
]);

const inviteNotificationTypes = new Set([
  "invite_join_group_6666",
  "invite_first_deposit_50000",
  "invite_second_stage_30000",
  "invite_third_deposit_10000"
]);

const newbieNotificationTypes = new Set([
  "newbie_step1_70",
  "newbie_step2_10",
  "newbie_step3_second_deposit_80",
  "newbie_step4_quiz_10",
  "newbie_step5_third_deposit_50",
  "newbie_step7_final_10",
  "newbie_step7_graduation_gift"
]);

const manualCompensationNotificationTypes = new Set([
  "manual_compensation",
  "gift_helper_bonus",
  "birthday_bonus_manual",
  "rescue_fund_monthly"
]);

function filterRewardNotifications(rows: RewardNotificationRow[], category: string, status: string, dateFilter: string, query: string) {
  const normalizedQuery = query.trim().toLowerCase();
  return rows.filter((row) => {
    if (category && category !== "all" && !notificationCategoryKeys(row.reward_type).includes(category)) return false;
    if (status === "notified" && !isNotificationMarked(row)) return false;
    if (status === "unnotified" && isNotificationMarked(row)) return false;
    if (!matchesNotificationDate(row, dateFilter)) return false;
    if (!normalizedQuery) return true;
    const fields = [
      row.member_account,
      String(row.reward_id),
      String(row.task_id),
      row.reward_type,
      rewardTypeLabel(row.reward_type),
      row.crawler_reference_id ?? "",
      notificationCategoryLabelsForReward(row.reward_type).join(" ")
    ];
    return fields.some((field) => String(field).toLowerCase().includes(normalizedQuery));
  });
}

function notificationCategoryKeys(rewardType: string) {
  const keys: string[] = [];
  if (highTouchNotificationTypes.has(rewardType)) keys.push("high_touch");
  if (depositNotificationTypes.has(rewardType)) keys.push("deposit_rebate");
  if (redPacketNotificationTypes.has(rewardType)) keys.push("red_packet");
  if (inviteNotificationTypes.has(rewardType)) keys.push("invite");
  if (newbieNotificationTypes.has(rewardType)) keys.push("newbie");
  if (manualCompensationNotificationTypes.has(rewardType)) keys.push("manual_compensation");
  return keys.length ? keys : ["all"];
}

function notificationCategoryLabelsForReward(rewardType: string) {
  return notificationCategoryKeys(rewardType)
    .filter((key) => key !== "all")
    .map(notificationCategoryLabel);
}

function notificationCategoryLabel(category: string) {
  return notificationCategoryOptions.find((option) => option.value === category)?.label ?? category;
}

function notificationStatusFilterLabel(status: string) {
  if (status === "notified") return "已通知";
  if (status === "unnotified") return "未通知";
  return "全部";
}

function notificationDateFilterLabel(filter: string) {
  if (filter === "today") return "今日";
  if (filter === "last7") return "近 7 天";
  return "全部";
}

function isNotificationMarked(row: RewardNotificationRow) {
  return row.notification_status === "notified" || row.notification_id != null;
}

function matchesNotificationDate(row: RewardNotificationRow, filter: string) {
  if (!filter || filter === "all") return true;
  const rawDate = row.completed_at ?? row.claimed_at;
  if (!rawDate) return false;
  const date = new Date(rawDate);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  if (filter === "today") {
    return date.getFullYear() === now.getFullYear()
      && date.getMonth() === now.getMonth()
      && date.getDate() === now.getDate();
  }
  if (filter === "last7") {
    const diff = now.getTime() - date.getTime();
    return diff >= 0 && diff <= 7 * 24 * 60 * 60 * 1000;
  }
  return true;
}

function notificationMessage(row: RewardNotificationRow) {
  if (row.reward_type === "birthday_bonus_manual") {
    return "您好，系統已為您派發生日禮金，點數已可使用，祝您生日好運加倍！";
  }
  if (row.reward_type === "rescue_fund_monthly") {
    return "您好，系統已為您補上回穩補給金，點數已可使用，祝您今天順利回穩！";
  }
  if (redPacketNotificationTypes.has(row.reward_type)) {
    return "您好，您的紅包獎勵已派發完成，點數已可使用，趕快進來試手氣！";
  }
  if (depositNotificationTypes.has(row.reward_type)) {
    return "您好，您的充值回饋已派發完成，點數已可使用。";
  }
  if (inviteNotificationTypes.has(row.reward_type)) {
    return "您好，您的邀友獎勵已派發完成，點數已可使用，繼續邀請朋友還能再拿更多獎勵！";
  }
  if (newbieNotificationTypes.has(row.reward_type)) {
    return "您好，您的新人任務獎勵已派發完成，點數已可使用，祝您接下來順利通關！";
  }
  return "您好，系統已為您派發活動獎勵，點數已可使用，祝您今天好運加倍！";
}

async function copyTextSafely(text: string) {
  const value = text.trim();
  if (!value) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Fall through to textarea fallback.
  }
  return copyTextWithTextarea(value);
}

function copyTextWithTextarea(text: string) {
  if (typeof document === "undefined") return false;
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "-9999px";
  textarea.style.opacity = "0";
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  textarea.setSelectionRange(0, text.length);
  try {
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

function RewardCrawlerDispatchTaskCards({
  tasks,
  emptyMessage,
  isLocalDev,
  processingTaskId,
  onRequestAction
}: {
  tasks: RewardCrawlerDispatchTaskRow[];
  emptyMessage: string;
  isLocalDev: boolean;
  processingTaskId: number | null;
  onRequestAction: (task: RewardCrawlerDispatchTaskRow, action: RewardDispatchTaskAction) => void;
}) {
  return (
    <div className="crawler-task-card-list">
      {tasks.map((task) => (
        <article className="crawler-task-card" key={task.id}>
          <div className="crawler-task-card-main">
            <div className="crawler-task-card-title">
              <div>
                <span className="crawler-task-eyebrow">任務 #{task.id} / 獎勵 #{task.reward_id}</span>
                <h3>{rewardTypeLabel(task.reward_type)}</h3>
              </div>
              <strong>{money(task.amount)}</strong>
            </div>

            <div className="crawler-task-badges">
              {renderCrawlerTaskStatus(task)}
              {task.dispatch_status === "pending" ? <span className="status-pill muted">{crawlerDispatchAvailabilityLabel(task)}</span> : null}
              {taskHasReachedMaxAttempts(task) ? <span className="status-pill danger">已達最大重試次數</span> : null}
            </div>

            <div className="crawler-task-summary-grid">
              <TaskSummaryItem label="會員帳號">
                <a className="admin-table-link" href={`/admin/members/${encodeURIComponent(task.member_account)}`}>
                  {task.member_account}
                </a>
              </TaskSummaryItem>
              <TaskSummaryItem label="派發模式">{crawlerDispatchModeLabel(task.dispatch_mode)}</TaskSummaryItem>
              <TaskSummaryItem label="嘗試次數">{`${taskAttemptCount(task)} / ${MAX_ATTEMPT_COUNT}`}</TaskSummaryItem>
              <TaskSummaryItem label="排程時間">{task.scheduled_at ? shortDate(task.scheduled_at) : "立即可派發"}</TaskSummaryItem>
              <TaskSummaryItem label="最近嘗試">{task.last_attempt_at ? shortDate(task.last_attempt_at) : "-"}</TaskSummaryItem>
              <TaskSummaryItem label="獎勵資料">
                <a className="admin-table-link" href={`/admin/rewards?member_account=${encodeURIComponent(task.member_account)}`}>
                  查看獎勵 #{task.reward_id}
                </a>
              </TaskSummaryItem>
            </div>

            <p className={task.dispatch_status === "failed" || isTaskProcessingTimedOut(task) ? "crawler-task-next danger" : "crawler-task-next"}>
              {crawlerTaskNextStep(task)}
            </p>
          </div>

          <div className="crawler-task-card-actions">
            {renderTaskActions(task, isLocalDev, processingTaskId, onRequestAction)}
          </div>

          <details className="crawler-task-details">
            <summary>展開明細</summary>
            <div className="crawler-task-detail-grid">
              <TaskDetailItem label="任務 ID" value={`#${task.id}`} />
              <TaskDetailItem label="獎勵 ID" value={`#${task.reward_id}`} />
              <TaskDetailItem label="獎勵類型" value={rewardTypeLabel(task.reward_type)} />
              <TaskDetailItem label="派發模式" value={crawlerDispatchModeLabel(task.dispatch_mode)} />
              <TaskDetailItem label="任務狀態" value={crawlerTaskStatusText(task)} />
              <TaskDetailItem label="預計派發時間" value={formatTaskDetailValue(task.scheduled_at, true)} />
              <TaskDetailItem label="排入時間" value={formatTaskDetailValue(task.queued_at, true)} />
              <TaskDetailItem label="開始處理時間" value={formatTaskDetailValue(task.started_at, true)} />
              <TaskDetailItem label="最近嘗試時間" value={formatTaskDetailValue(task.last_attempt_at, true)} />
              <TaskDetailItem label="完成時間" value={formatTaskDetailValue(task.completed_at, true)} />
              <TaskDetailItem label="失敗時間" value={formatTaskDetailValue(task.failed_at, true)} />
              <TaskDetailItem label="失敗原因" value={formatTaskDetailValue(task.failed_reason)} wide />
              <TaskDetailItem label="嘗試次數" value={String(taskAttemptCount(task))} />
              <TaskDetailItem label="爬蟲回傳編號" value={formatCrawlerReferenceDisplay(task.crawler_reference_id)} />
            </div>
            <details className="raw-details crawler-task-raw">
              <summary>查看原始資料</summary>
              <p className="admin-muted">原始資料僅供工程查錯使用，客服日常可忽略。</p>
              <pre>{prettyRawJson({
                派發任務資料: parseTaskJson(task.crawler_payload_json),
                回寫資料: parseTaskJson(task.crawler_response_json)
              })}</pre>
            </details>
          </details>
        </article>
      ))}
      {!tasks.length ? <p className="crawler-task-empty">{emptyMessage}</p> : null}
    </div>
  );
}

function TaskSummaryItem({ label, children }: { label: string; children: string | number | JSX.Element }) {
  return (
    <div className="crawler-task-summary-item">
      <span>{label}</span>
      <strong>{children}</strong>
    </div>
  );
}

function TaskDetailItem({ label, value, wide = false }: { label: string; value: string; wide?: boolean }) {
  return (
    <div className={wide ? "crawler-task-detail-item wide" : "crawler-task-detail-item"}>
      <span>{label}</span>
      <strong>{value || "-"}</strong>
    </div>
  );
}

function crawlerTaskNextStep(task: RewardCrawlerDispatchTaskRow) {
  if (task.dispatch_status === "pending") {
    if (task.dispatch_mode === "next_day_12_crawler_dispatch" && task.scheduled_at && !isTaskCurrentlyClaimable(task)) {
      return "尚未到派發時間，系統不會提前交給贈禮小幫手。";
    }
    return "等待贈禮小幫手處理。";
  }
  if (task.dispatch_status === "processing") {
    if (isTaskProcessingTimedOut(task)) {
      return `處理逾時，已超過 ${PROCESSING_TIMEOUT_MINUTES} 分鐘未回寫，請確認外部爬蟲狀態後處理。`;
    }
    return "派發中，請等待贈禮小幫手回寫結果。";
  }
  if (task.dispatch_status === "failed") {
    return "派發失敗，請查看原因後選擇重新排入、取消任務，或確認外部已處理後標記人工確認完成。";
  }
  if (task.dispatch_status === "canceled") {
    return "此任務已取消，不會交給贈禮小幫手處理。可重新排入或人工確認完成。";
  }
  if (task.dispatch_status === "completed") {
    return "已派發完成，不需要再操作。";
  }
  return "請依目前任務狀態處理。";
}

function formatTaskDetailValue(value?: string | number | null, isDate = false) {
  if (value == null || value === "") return "-";
  return isDate ? shortDate(String(value)) : String(value);
}

function formatCrawlerReferenceDisplay(value?: string | null) {
  const displayValue = formatTaskDetailValue(value);
  if (displayValue === "-") return displayValue;
  return displayValue
    .replace(/\bpending\b/g, "等待派發")
    .replace(/\bprocessing\b/g, "派發中")
    .replace(/\bcompleted\b/g, "已完成")
    .replace(/\bfailed\b/g, "失敗")
    .replace(/\bcanceled\b/g, "已取消");
}

function renderTaskActions(
  task: RewardCrawlerDispatchTaskRow,
  isLocalDev: boolean,
  processingTaskId: number | null,
  onRequestAction: (task: RewardCrawlerDispatchTaskRow, action: RewardDispatchTaskAction) => void
) {
  const processing = processingTaskId === task.id;
  const isTimedOut = isTaskProcessingTimedOut(task);
  const maxAttemptsReached = taskHasReachedMaxAttempts(task);

  if (task.dispatch_status === "completed") {
    return <p className="admin-muted">已派發完成，不需再操作。</p>;
  }

  if (task.dispatch_status === "processing" && !isTimedOut) {
    return (
      <div className="summary-list">
        <p className="admin-muted">派發中，等待贈禮小幫手回寫結果。</p>
      </div>
    );
  }

  if (task.dispatch_status === "processing" && isTimedOut) {
    return (
      <div className="summary-list">
        <p className="error-text">處理逾時，已超過 {PROCESSING_TIMEOUT_MINUTES} 分鐘未回寫。</p>
        <div className="cta-row compact-actions">
          <button
            className="admin-btn admin-btn-action"
            type="button"
            disabled={processing || maxAttemptsReached}
            onClick={() => onRequestAction(task, "retry")}
          >
            重新排入
          </button>
          <button className="admin-btn admin-btn-danger" type="button" disabled={processing} onClick={() => onRequestAction(task, "manual_completed")}>
            人工確認完成
          </button>
          <button className="admin-btn admin-btn-danger" type="button" disabled={processing} onClick={() => onRequestAction(task, "cancel")}>
            取消任務
          </button>
        </div>
        {maxAttemptsReached ? <p className="error-text">已達最大重試次數，請改由人工處理。</p> : null}
      </div>
    );
  }

  if (task.dispatch_status === "failed") {
    return (
      <div className="summary-list">
        <div className="cta-row compact-actions">
          <button
            className="admin-btn admin-btn-action"
            type="button"
            disabled={processing || maxAttemptsReached}
            onClick={() => onRequestAction(task, "retry")}
          >
            重新排入
          </button>
          <button className="admin-btn admin-btn-danger" type="button" disabled={processing} onClick={() => onRequestAction(task, "manual_completed")}>
            人工確認完成
          </button>
          <button className="admin-btn admin-btn-danger" type="button" disabled={processing} onClick={() => onRequestAction(task, "cancel")}>
            取消任務
          </button>
        </div>
        {maxAttemptsReached ? <p className="error-text">已達最大重試次數，請改由人工處理。</p> : null}
      </div>
    );
  }

  if (task.dispatch_status === "pending") {
    const canSimulate = isLocalDev && isTaskCurrentlyClaimable(task);
    return (
      <div className="cta-row compact-actions">
        {canSimulate ? renderLocalDevSimulateButtons(task, processing, onRequestAction) : null}
        {isLocalDev && !canSimulate ? <span className="admin-muted">尚未到派發時間，不能模擬派發。</span> : null}
        <button className="admin-btn admin-btn-danger" type="button" disabled={processing} onClick={() => onRequestAction(task, "cancel")}>
          取消任務
        </button>
        <button className="admin-btn admin-btn-danger" type="button" disabled={processing} onClick={() => onRequestAction(task, "manual_completed")}>
          人工確認完成
        </button>
      </div>
    );
  }

  if (task.dispatch_status === "canceled") {
    return (
      <div className="summary-list">
        <div className="cta-row compact-actions">
          <button
            className="admin-btn admin-btn-action"
            type="button"
            disabled={processing || maxAttemptsReached}
            onClick={() => onRequestAction(task, "retry")}
          >
            重新排入
          </button>
          <button className="admin-btn admin-btn-danger" type="button" disabled={processing} onClick={() => onRequestAction(task, "manual_completed")}>
            人工確認完成
          </button>
        </div>
        {maxAttemptsReached ? <p className="error-text">已達最大重試次數，請改由人工處理。</p> : null}
      </div>
    );
  }

  return <p className="admin-muted">目前狀態無可用操作。</p>;
}

function renderLocalDevSimulateActions(
  task: RewardCrawlerDispatchTaskRow,
  processing: boolean,
  onRequestAction: (task: RewardCrawlerDispatchTaskRow, action: RewardDispatchTaskAction) => void
) {
  return (
    <div className="cta-row compact-actions">
      {renderLocalDevSimulateButtons(task, processing, onRequestAction)}
    </div>
  );
}

function renderLocalDevSimulateButtons(
  task: RewardCrawlerDispatchTaskRow,
  processing: boolean,
  onRequestAction: (task: RewardCrawlerDispatchTaskRow, action: RewardDispatchTaskAction) => void
) {
  return (
    <>
      <button className="admin-btn admin-btn-action" type="button" disabled={processing} onClick={() => onRequestAction(task, "simulate_completed")}>
        local/dev 模擬完成
      </button>
      <button className="admin-btn admin-btn-danger" type="button" disabled={processing} onClick={() => onRequestAction(task, "simulate_failed")}>
        local/dev 模擬失敗
      </button>
    </>
  );
}

function isTaskCurrentlyClaimable(task: Pick<RewardCrawlerDispatchTaskRow, "dispatch_status" | "scheduled_at">) {
  if (task.dispatch_status !== "pending") return false;
  if (!task.scheduled_at) return true;
  const scheduledAt = new Date(task.scheduled_at).getTime();
  return Number.isFinite(scheduledAt) && scheduledAt <= Date.now();
}

function taskAttemptCount(task: Pick<RewardCrawlerDispatchTaskRow, "attempt_count">) {
  const count = Number(task.attempt_count ?? 0);
  return Number.isFinite(count) ? count : 0;
}

function taskHasReachedMaxAttempts(task: Pick<RewardCrawlerDispatchTaskRow, "attempt_count">) {
  return taskAttemptCount(task) >= MAX_ATTEMPT_COUNT;
}

function isTaskProcessingTimedOut(
  task: Pick<RewardCrawlerDispatchTaskRow, "dispatch_status" | "last_attempt_at" | "started_at">
) {
  if (task.dispatch_status !== "processing") return false;
  const basis = task.last_attempt_at || task.started_at;
  if (!basis) return false;
  const basisTime = new Date(basis).getTime();
  if (!Number.isFinite(basisTime)) return false;
  return basisTime <= Date.now() - PROCESSING_TIMEOUT_MINUTES * 60 * 1000;
}

function taskActionEndpoint(taskId: number, action: RewardDispatchTaskAction) {
  if (action === "retry") return `/api/admin/crawler/reward-dispatch-tasks/${taskId}/retry`;
  if (action === "cancel") return `/api/admin/crawler/reward-dispatch-tasks/${taskId}/cancel`;
  if (action === "simulate_completed") return `/api/admin/crawler/reward-dispatch-tasks/${taskId}/simulate-completed`;
  if (action === "simulate_failed") return `/api/admin/crawler/reward-dispatch-tasks/${taskId}/simulate-failed`;
  return `/api/admin/crawler/reward-dispatch-tasks/${taskId}/mark-manual-completed`;
}

function taskActionBody(action: RewardDispatchTaskAction) {
  if (action === "manual_completed") {
    return JSON.stringify({ note: "摰Ｘ?鈭箏極蝣箄?韐旨撠鼠?晷?澆???" });
  }
  if (action === "simulate_completed") {
    return JSON.stringify({ worker_id: "local-dev-admin", note: "local/dev 模擬派發完成" });
  }
  if (action === "simulate_failed") {
    return JSON.stringify({ worker_id: "local-dev-admin", note: "local/dev 模擬派發失敗", failed_reason: "local/dev simulated failed" });
  }
  return undefined;
}

function taskActionSuccessMessage(action: RewardDispatchTaskAction, taskId: number) {
  if (action === "retry") return `派發任務 #${taskId} 已重新排入。`;
  if (action === "cancel") return `派發任務 #${taskId} 已取消。`;
  return `派發任務 #${taskId} 已人工確認完成。`;
}

function taskActionConfirmCopy(action: RewardDispatchTaskAction) {
  if (action === "simulate_completed") {
    return {
      title: "確認 local/dev 模擬派發完成？",
      message: "系統會先 claim 任務，再用本機模擬結果把任務標記為已派發完成。",
      confirmText: "確認模擬完成"
    };
  }
  if (action === "simulate_failed") {
    return {
      title: "確認 local/dev 模擬派發失敗？",
      message: "系統會先 claim 任務，再用本機模擬結果把任務標記為派發失敗，獎勵仍會保留待派發。",
      confirmText: "確認模擬失敗"
    };
  }
  if (action === "retry") {
    return {
      title: "確認重新排入？",
      message: "這會將此派發任務重新排入待派發，獎勵仍保持待派發，等待贈禮小幫手回寫結果。",
      confirmText: "重新排入"
    };
  }
  if (action === "cancel") {
    return {
      title: "確認取消任務？",
      message: "取消後此任務不會交給贈禮小幫手處理，獎勵仍保持待派發，可再由人工處理。",
      confirmText: "取消任務"
    };
  }
  return {
    title: "確認人工標記完成？",
    message: "請確認獎勵已實際入帳。此操作會把獎勵與派發任務標記為已派發完成，並寫入系統紀錄。",
    confirmText: "人工確認完成"
  };
}

function taskActionSuccessMessageSafe(action: RewardDispatchTaskAction, taskId: number) {
  if (action === "simulate_completed") return `local/dev 已模擬派發完成 #${taskId}。`;
  if (action === "simulate_failed") return `local/dev 已模擬派發失敗 #${taskId}。`;
  return taskActionSuccessMessage(action, taskId);
}

function taskActionConfirmCopySafe(action: RewardDispatchTaskAction) {
  if (action === "simulate_completed") {
    return {
      title: "確認 local/dev 模擬派發完成？",
      message: "此操作會先 claim 任務，再模擬爬蟲 completed callback。僅供 local/dev 驗收使用。",
      confirmText: "確認模擬完成"
    };
  }
  if (action === "simulate_failed") {
    return {
      title: "確認 local/dev 模擬派發失敗？",
      message: "此操作會先 claim 任務，再模擬爬蟲 failed callback。reward 會保持待派發，方便測試重試流程。",
      confirmText: "確認模擬失敗"
    };
  }
  return taskActionConfirmCopy(action);
}

function filterRewardCrawlerTasks(tasks: RewardCrawlerDispatchTaskRow[], status: string, query: string) {
  const normalizedQuery = query.trim().toLowerCase();
  return tasks.filter((task) => {
    const statusMatched = !status || status === "all" || task.dispatch_status === status;
    if (!statusMatched) return false;
    if (!normalizedQuery) return true;
    const fields = [
      task.member_account,
      String(task.reward_id),
      String(task.id),
      task.reward_type,
      rewardTypeLabel(task.reward_type),
      task.crawler_reference_id ?? "",
      task.failed_reason ?? ""
    ];
    return fields.some((field) => String(field).toLowerCase().includes(normalizedQuery));
  });
}

function crawlerTaskStatusFilterLabel(status: string) {
  if (status === "all") return "全部";
  return crawlerDispatchStatusLabel(status);
}

function renderCrawlerTaskStatus(task: RewardCrawlerDispatchTaskRow) {
  if (task.dispatch_status === "processing" && isTaskProcessingTimedOut(task)) {
    return <span className="status-pill danger">處理逾時</span>;
  }
  return <span className={`status-pill ${crawlerDispatchStatusTone(task.dispatch_status)}`}>{crawlerDispatchStatusLabel(task.dispatch_status)}</span>;
}

function crawlerTaskStatusText(task: RewardCrawlerDispatchTaskRow) {
  if (task.dispatch_status === "processing" && isTaskProcessingTimedOut(task)) return "處理逾時";
  return crawlerDispatchStatusLabel(task.dispatch_status);
}

function crawlerDispatchStatusTone(status: string) {
  if (status === "pending") return "soon";
  if (status === "processing") return "warn";
  if (status === "completed") return "good";
  if (status === "failed") return "danger";
  if (status === "canceled") return "muted";
  return "muted";
}

function crawlerDispatchStatusLabel(status: string) {
  if (status === "pending") return "等待派發";
  if (status === "processing") return "派發中";
  if (status === "completed") return "已派發完成";
  if (status === "failed") return "派發失敗";
  if (status === "canceled") return "已取消";
  return status;
}

function crawlerDispatchModeLabel(mode: string) {
  if (mode === "immediate_crawler_dispatch") return "立即排入";
  if (mode === "next_day_12_crawler_dispatch") return "明日 12 點後排入";
  if (mode === "manual_confirm_then_crawler_dispatch") return "人工確認後排入";
  return mode;
}

function crawlerDispatchAvailabilityLabel(task: Pick<RewardCrawlerDispatchTaskRow, "dispatch_status" | "dispatch_mode" | "scheduled_at">) {
  if (task.dispatch_status !== "pending") return "-";
  if (task.dispatch_mode === "next_day_12_crawler_dispatch" && task.scheduled_at) {
    const scheduledAt = new Date(task.scheduled_at).getTime();
    if (Number.isFinite(scheduledAt) && scheduledAt > Date.now()) return "尚未到派發時間";
  }
  return "可派發";
}

function parseTaskJson(raw?: string | null) {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function giftRecordStatus(row: RawGiftRecordRow) {
  if (row.reward_status === "completed") {
    return (
      <span>
        已派發完成：派發 #{row.reward_id} 已完成
        <br />
        <a className="admin-table-link" href={`/admin/rewards?member_account=${encodeURIComponent(row.member_account)}`}>派發 #{row.reward_id}</a>
      </span>
    );
  }
  if (row.reward_id) {
    return (
      <span>
        已轉入派發中心：獎勵 #{row.reward_id}｜{rewardStatusLabel(row.reward_status)}
        <br />
        <a className="admin-table-link" href={`/admin/rewards?member_account=${encodeURIComponent(row.member_account)}`}>派發 #{row.reward_id}</a>
      </span>
    );
  }
  if (row.review_id) {
    return (
      <span>
        已建立審核：等待確認審核 #{row.review_id}｜{reviewStatusLabel(row.review_status)}
        <br />
        <a className="admin-table-link" href={`/admin/reviews?member_account=${encodeURIComponent(row.member_account)}`}>審核 #{row.review_id}</a>
      </span>
    );
  }
  if (!row.external_id) return <span className="error-text">缺少外部紀錄 ID</span>;
  if (isMissingMember(row)) return <span className="error-text">找不到會員資料，無法建立審核</span>;
  if (row.gift_type !== "gift_helper") return <span className="error-text">此贈禮類型不支援建立派發審核</span>;
  return <span>未處理</span>;
}

function giftRecordOperationLabel(row: RawGiftRecordRow) {
  if (!row.external_id) return "不可建立審核";
  if (isMissingMember(row)) return "不可建立審核";
  if (row.gift_type !== "gift_helper") return "不可建立審核";
  if (row.reward_id || row.review_id) return "不可重複操作";
  return "不可建立審核";
}

function isMissingMember(row: RawGiftRecordRow) {
  return row.member_exists === false || row.member_exists === 0;
}

function giftTypeLabel(type: string) {
  if (type === "gift_helper") return "贈禮小幫手";
  if (type === "unsupported_gift_type") return "不支援的贈禮類型";
  return type;
}
